import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DropletIcon, FlameIcon, BeakerIcon, ShieldIcon, BarChartIcon } from 'lucide-react';
const HowItWorks = () => {
  const [activeSection, setActiveSection] = useState('method');
  return <div className="w-full bg-white pt-24" data-id="element-716">
      <div className="container mx-auto px-4 py-16" data-id="element-717">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-4" data-id="element-718">
          The Science of Spora
        </h1>
        <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-16" data-id="element-719">
          The first commercial-scale system for full-spectrum mushroom
          extraction
        </p>
        {/* Desktop: Sticky side navigation */}
        <div className="hidden md:flex" data-id="element-720">
          <div className="w-64 sticky top-32 self-start pr-8" data-id="element-721">
            <nav className="flex flex-col space-y-4" data-id="element-722">
              <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'method' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('method')} data-id="element-723">
                The Innovation
              </button>
              <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'chemistry' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('chemistry')} data-id="element-724">
                The Dual-Extraction Science
              </button>
              <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'safety' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('safety')} data-id="element-725">
                Commercial Applications
              </button>
            </nav>
          </div>
          <div className="flex-1 max-w-3xl" data-id="element-726">
            {/* The Method Section */}
            <section id="method" className={`pb-24 ${activeSection === 'method' ? 'block' : 'hidden'}`} data-id="element-727">
              <h2 className="text-3xl font-bold mb-8" data-id="element-728">The Innovation</h2>
              <p className="text-lg text-gray-600 mb-12" data-id="element-729">
                Spora's proprietary three-phase extraction technology creates
                the first standardized, shelf-stable functional beverage that
                captures the full spectrum of mushroom bioactives.
              </p>
              <div className="grid grid-cols-1 gap-12" data-id="element-730">
                <div className="flex flex-col md:flex-row gap-8" data-id="element-731">
                  <div className="md:w-16 flex md:flex-col items-center" data-id="element-732">
                    <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4" data-id="element-733">
                      <DropletIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-734" />
                    </div>
                    <div className="hidden md:block w-0.5 h-full bg-gray-200 mx-auto mt-4" data-id="element-735"></div>
                  </div>
                  <div data-id="element-736">
                    <h3 className="text-xl font-bold mb-3" data-id="element-737">
                      Phase 1: Precision Alcohol Extraction
                    </h3>
                    <p className="text-gray-600 mb-4" data-id="element-738">
                      Our process begins with calibrated spirit infusion that
                      targets and extracts triterpenes, sterols, and other
                      compounds that are insoluble in water alone. This critical
                      phase—missing from conventional mushroom products—unlocks
                      the full therapeutic potential of medicinal fungi.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-xl" data-id="element-739">
                      <p className="text-sm font-medium" data-id="element-740">
                        Proprietary calibration technology
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-741">
                        Optimized for maximum bioactive compound extraction
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-8" data-id="element-742">
                  <div className="md:w-16 flex md:flex-col items-center" data-id="element-743">
                    <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4" data-id="element-744">
                      <FlameIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-745" />
                    </div>
                    <div className="hidden md:block w-0.5 h-full bg-gray-200 mx-auto mt-4" data-id="element-746"></div>
                  </div>
                  <div data-id="element-747">
                    <h3 className="text-xl font-bold mb-3" data-id="element-748">
                      Phase 2: Thermal Cell Wall Disruption
                    </h3>
                    <p className="text-gray-600 mb-4" data-id="element-749">
                      Our precision thermal application breaks down the chitin
                      cell walls of the mushroom, releasing water-soluble
                      beta-glucans and polysaccharides. This controlled process
                      simultaneously initiates alcohol evaporation, preserving
                      only the beneficial compounds.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-xl" data-id="element-750">
                      <p className="text-sm font-medium" data-id="element-751">
                        Temperature-controlled extraction matrix
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-752">
                        Engineered for consistent, reproducible results
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-8" data-id="element-753">
                  <div className="md:w-16 flex md:flex-col items-center" data-id="element-754">
                    <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4" data-id="element-755">
                      <BeakerIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-756" />
                    </div>
                  </div>
                  <div data-id="element-757">
                    <h3 className="text-xl font-bold mb-3" data-id="element-758">
                      Phase 3: Complete Compound Expression
                    </h3>
                    <p className="text-gray-600 mb-4" data-id="element-759">
                      The final phase employs calibrated pressure to express the
                      full spectrum of compounds from the mushroom material.
                      This creates a standardized, bioavailable infusion with
                      precise dosing and consistent effects—impossible with
                      conventional extraction methods.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-xl" data-id="element-760">
                      <p className="text-sm font-medium" data-id="element-761">
                        Standardized extraction protocol
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-762">
                        Yields consistent potency and bioavailability across
                        production batches
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {/* The Chemistry Section */}
            <section id="chemistry" className={`pb-24 ${activeSection === 'chemistry' ? 'block' : 'hidden'}`} data-id="element-763">
              <h2 className="text-3xl font-bold mb-8" data-id="element-764">
                The Dual-Extraction Science
              </h2>
              <p className="text-lg text-gray-600 mb-12" data-id="element-765">
                Spora's category-defining innovation lies in our ability to
                capture both water and alcohol-soluble compounds in a single
                product—a feat no other functional beverage has achieved at
                scale.
              </p>
              <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm mb-12" data-id="element-766">
                <h3 className="text-xl font-bold mb-4" data-id="element-767">
                  The Extraction Gap in Functional Beverages
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-768">
                  <div className="text-center p-4" data-id="element-769">
                    <h4 className="font-bold mb-2" data-id="element-770">Coffee</h4>
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-771">
                      <div className="h-full bg-blue-500 rounded-full" style={{
                      width: '50%'
                    }} data-id="element-772"></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-773">
                      Water-soluble only
                    </p>
                  </div>
                  <div className="text-center p-4" data-id="element-774">
                    <h4 className="font-bold mb-2" data-id="element-775">Tea</h4>
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-776">
                      <div className="h-full bg-green-500 rounded-full" style={{
                      width: '60%'
                    }} data-id="element-777"></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-778">
                      Water-soluble only
                    </p>
                  </div>
                  <div className="text-center p-4" data-id="element-779">
                    <h4 className="font-bold mb-2" data-id="element-780">Spora</h4>
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-781">
                      <div className="h-full bg-red-500 rounded-full" style={{
                      width: '95%'
                    }} data-id="element-782"></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-783">
                      Full-spectrum extraction
                    </p>
                  </div>
                </div>
                <p className="text-gray-600" data-id="element-784">
                  Coffee and tea capture only water-soluble compounds. Spora's
                  dual-extraction technology captures both water and
                  alcohol-soluble compounds, delivering a complete therapeutic
                  profile.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-785">
                <div className="bg-gray-50 p-6 rounded-xl" data-id="element-786">
                  <h3 className="text-xl font-bold mb-4" data-id="element-787">
                    Water-Soluble Compounds
                  </h3>
                  <ul className="space-y-3 text-gray-600" data-id="element-788">
                    <li className="flex items-start gap-2" data-id="element-789">
                      <div className="w-1.5 h-1.5 rounded-full bg-black mt-2" data-id="element-790"></div>
                      <span data-id="element-791">
                        <strong data-id="element-792">Beta-glucans:</strong> Immune-modulating
                        polysaccharides that support immune system function
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-793">
                      <div className="w-1.5 h-1.5 rounded-full bg-black mt-2" data-id="element-794"></div>
                      <span data-id="element-795">
                        <strong data-id="element-796">Glycoproteins:</strong> Support cellular
                        communication and immune response
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-797">
                      <div className="w-1.5 h-1.5 rounded-full bg-black mt-2" data-id="element-798"></div>
                      <span data-id="element-799">
                        <strong data-id="element-800">Phenolic compounds:</strong> Powerful
                        antioxidants that combat oxidative stress
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl" data-id="element-801">
                  <h3 className="text-xl font-bold mb-4" data-id="element-802">
                    Alcohol-Soluble Compounds
                  </h3>
                  <ul className="space-y-3 text-gray-600" data-id="element-803">
                    <li className="flex items-start gap-2" data-id="element-804">
                      <div className="w-1.5 h-1.5 rounded-full bg-black mt-2" data-id="element-805"></div>
                      <span data-id="element-806">
                        <strong data-id="element-807">Triterpenes:</strong> Anti-inflammatory and
                        adaptogenic compounds that modulate stress response
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-808">
                      <div className="w-1.5 h-1.5 rounded-full bg-black mt-2" data-id="element-809"></div>
                      <span data-id="element-810">
                        <strong data-id="element-811">Sterols:</strong> Support hormone balance and
                        cellular membrane integrity
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-812">
                      <div className="w-1.5 h-1.5 rounded-full bg-black mt-2" data-id="element-813"></div>
                      <span data-id="element-814">
                        <strong data-id="element-815">Terpenes:</strong> Contribute to flavor profile
                        and synergistic therapeutic effects
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm" data-id="element-816">
                <h3 className="text-xl font-bold mb-4" data-id="element-817">
                  The Evaporation Protocol
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-818">
                  Our proprietary evaporation protocol ensures that ethanol is
                  removed during processing while retaining 100% of the
                  beneficial compounds extracted during the alcohol phase.
                </p>
                <p className="text-gray-600" data-id="element-819">
                  This technical breakthrough allows Spora to deliver the
                  benefits of dual extraction in a non-alcoholic format suitable
                  for daily consumption—solving a challenge that has limited
                  mushroom extract efficacy for decades.
                </p>
              </div>
            </section>
            {/* Safety & Daily Use Section */}
            <section id="safety" className={`pb-24 ${activeSection === 'safety' ? 'block' : 'hidden'}`} data-id="element-820">
              <h2 className="text-3xl font-bold mb-8" data-id="element-821">
                Commercial Applications
              </h2>
              <p className="text-lg text-gray-600 mb-12" data-id="element-822">
                Spora's standardized production system creates scalable
                opportunities for distribution partners.
              </p>
              <div className="space-y-8" data-id="element-823">
                <div className="bg-gray-50 p-6 rounded-xl" data-id="element-824">
                  <div className="flex items-start gap-4 mb-4" data-id="element-825">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm" data-id="element-826">
                      <BarChartIcon className="w-6 h-6" data-id="element-827" />
                    </div>
                    <div data-id="element-828">
                      <h3 className="text-xl font-bold mb-2" data-id="element-829">
                        Standardized Dosing Protocol
                      </h3>
                      <p className="text-gray-600" data-id="element-830">
                        Each Spora product contains a precisely calibrated dose
                        of bioactive compounds, ensuring consistent effects for
                        consumers. This standardization enables clear marketing
                        claims and reliable customer experiences—critical for
                        category creation.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-831">
                    <h4 className="font-medium mb-2" data-id="element-832">Product Specifications</h4>
                    <ul className="space-y-2 text-sm text-gray-600" data-id="element-833">
                      <li className="flex items-start gap-2" data-id="element-834">
                        <div className="w-1.5 h-1.5 rounded-full bg-black mt-1.5" data-id="element-835"></div>
                        <span data-id="element-836">
                          <strong data-id="element-837">Active compounds:</strong> Standardized to 30%
                          beta-glucans and 2% triterpenes per serving
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-838">
                        <div className="w-1.5 h-1.5 rounded-full bg-black mt-1.5" data-id="element-839"></div>
                        <span data-id="element-840">
                          <strong data-id="element-841">Shelf stability:</strong> 18-month guaranteed
                          potency with no refrigeration required
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-842">
                        <div className="w-1.5 h-1.5 rounded-full bg-black mt-1.5" data-id="element-843"></div>
                        <span data-id="element-844">
                          <strong data-id="element-845">Batch consistency:</strong> Less than 5%
                          variation between production runs
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl" data-id="element-846">
                  <div className="flex items-start gap-4 mb-4" data-id="element-847">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm" data-id="element-848">
                      <ShieldIcon className="w-6 h-6" data-id="element-849" />
                    </div>
                    <div data-id="element-850">
                      <h3 className="text-xl font-bold mb-2" data-id="element-851">
                        Regulatory Compliance
                      </h3>
                      <p className="text-gray-600" data-id="element-852">
                        Spora's finished products contain less than 0.5% ABV,
                        meeting FDA standards for non-alcoholic beverages. This
                        classification enables broad distribution through
                        conventional food and beverage channels.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-853">
                    <h4 className="font-medium mb-2" data-id="element-854">Regulatory Framework</h4>
                    <ul className="space-y-2 text-sm text-gray-600" data-id="element-855">
                      <li className="flex items-center justify-between" data-id="element-856">
                        <span data-id="element-857">Spora finished product:</span>
                        <span className="font-medium" data-id="element-858">&lt;0.5% ABV</span>
                      </li>
                      <li className="flex items-center justify-between" data-id="element-859">
                        <span data-id="element-860">FDA classification:</span>
                        <span className="font-medium" data-id="element-861">
                          Non-alcoholic beverage
                        </span>
                      </li>
                      <li className="flex items-center justify-between" data-id="element-862">
                        <span data-id="element-863">Regulatory category:</span>
                        <span className="font-medium" data-id="element-864">Functional food</span>
                      </li>
                      <li className="flex items-center justify-between" data-id="element-865">
                        <span data-id="element-866">Distribution channels:</span>
                        <span className="font-medium" data-id="element-867">Unrestricted</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl" data-id="element-868">
                  <div className="flex items-start gap-4 mb-4" data-id="element-869">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm" data-id="element-870">
                      <div className="w-6 h-6 flex items-center justify-center" data-id="element-871">
                        <span className="text-lg font-bold" data-id="element-872">IP</span>
                      </div>
                    </div>
                    <div data-id="element-873">
                      <h3 className="text-xl font-bold mb-2" data-id="element-874">
                        Intellectual Property
                      </h3>
                      <p className="text-gray-600" data-id="element-875">
                        Spora's extraction process and formulations are
                        protected by pending patents and trade secrets, creating
                        a defensible market position. Our proprietary production
                        system enables partners to offer truly differentiated
                        products in the functional beverage category.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-876">
                    <h4 className="font-medium mb-2" data-id="element-877">IP Portfolio</h4>
                    <ul className="space-y-2 text-sm text-gray-600" data-id="element-878">
                      <li className="flex items-start gap-2" data-id="element-879">
                        <div className="w-1.5 h-1.5 rounded-full bg-black mt-1.5" data-id="element-880"></div>
                        <span data-id="element-881">
                          <strong data-id="element-882">Patents pending:</strong> Dual-extraction
                          process for shelf-stable functional mushroom beverages
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-883">
                        <div className="w-1.5 h-1.5 rounded-full bg-black mt-1.5" data-id="element-884"></div>
                        <span data-id="element-885">
                          <strong data-id="element-886">Proprietary formulations:</strong>{' '}
                          Spirit-mushroom pairings optimized for flavor and
                          function
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-887">
                        <div className="w-1.5 h-1.5 rounded-full bg-black mt-1.5" data-id="element-888"></div>
                        <span data-id="element-889">
                          <strong data-id="element-890">Trade secrets:</strong> Production techniques
                          and quality control methodologies
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
        {/* Mobile: Collapsible sections */}
        <div className="md:hidden space-y-8" data-id="element-891">
          <div data-id="element-892">
            <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'method' ? '' : 'method')} data-id="element-893">
              <h2 className="text-xl font-bold" data-id="element-894">The Innovation</h2>
              <span data-id="element-895">{activeSection === 'method' ? '−' : '+'}</span>
            </button>
            {activeSection === 'method' && <div className="p-4" data-id="element-896">
                <p className="text-gray-600 mb-8" data-id="element-897">
                  Spora's proprietary three-phase extraction technology creates
                  the first standardized, shelf-stable functional beverage that
                  captures the full spectrum of mushroom bioactives.
                </p>
                <div className="space-y-8" data-id="element-898">
                  <div data-id="element-899">
                    <div className="flex items-center gap-4 mb-4" data-id="element-900">
                      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full" data-id="element-901">
                        <DropletIcon className="w-6 h-6" data-id="element-902" />
                      </div>
                      <h3 className="text-lg font-bold" data-id="element-903">
                        Phase 1: Precision Alcohol Extraction
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4" data-id="element-904">
                      Calibrated spirit infusion extracts compounds insoluble in
                      water alone, unlocking the full therapeutic potential.
                    </p>
                  </div>
                  <div data-id="element-905">
                    <div className="flex items-center gap-4 mb-4" data-id="element-906">
                      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full" data-id="element-907">
                        <FlameIcon className="w-6 h-6" data-id="element-908" />
                      </div>
                      <h3 className="text-lg font-bold" data-id="element-909">
                        Phase 2: Thermal Cell Wall Disruption
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4" data-id="element-910">
                      Precision thermal application breaks down chitin cell
                      walls, releasing water-soluble compounds.
                    </p>
                  </div>
                  <div data-id="element-911">
                    <div className="flex items-center gap-4 mb-4" data-id="element-912">
                      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full" data-id="element-913">
                        <BeakerIcon className="w-6 h-6" data-id="element-914" />
                      </div>
                      <h3 className="text-lg font-bold" data-id="element-915">
                        Phase 3: Complete Compound Expression
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4" data-id="element-916">
                      Calibrated pressure creates a standardized, bioavailable
                      infusion with precise dosing.
                    </p>
                  </div>
                </div>
              </div>}
          </div>
          <div data-id="element-917">
            <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'chemistry' ? '' : 'chemistry')} data-id="element-918">
              <h2 className="text-xl font-bold" data-id="element-919">The Dual-Extraction Science</h2>
              <span data-id="element-920">{activeSection === 'chemistry' ? '−' : '+'}</span>
            </button>
            {activeSection === 'chemistry' && <div className="p-4" data-id="element-921">
                <p className="text-gray-600 mb-8" data-id="element-922">
                  Spora's innovation captures both water and alcohol-soluble
                  compounds in a single product—a feat no other functional
                  beverage has achieved at scale.
                </p>
                <div className="space-y-6" data-id="element-923">
                  <div className="bg-gray-50 p-4 rounded-xl" data-id="element-924">
                    <h3 className="text-lg font-bold mb-3" data-id="element-925">
                      Water-Soluble Compounds
                    </h3>
                    <ul className="space-y-2 text-gray-600 text-sm" data-id="element-926">
                      <li data-id="element-927">Beta-glucans: Immune-modulating polysaccharides</li>
                      <li data-id="element-928">Glycoproteins: Support cellular communication</li>
                      <li data-id="element-929">Phenolic compounds: Powerful antioxidants</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl" data-id="element-930">
                    <h3 className="text-lg font-bold mb-3" data-id="element-931">
                      Alcohol-Soluble Compounds
                    </h3>
                    <ul className="space-y-2 text-gray-600 text-sm" data-id="element-932">
                      <li data-id="element-933">Triterpenes: Anti-inflammatory and adaptogenic</li>
                      <li data-id="element-934">Sterols: Support hormone balance</li>
                      <li data-id="element-935">Terpenes: Enhance flavor and effects</li>
                    </ul>
                  </div>
                </div>
              </div>}
          </div>
          <div data-id="element-936">
            <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'safety' ? '' : 'safety')} data-id="element-937">
              <h2 className="text-xl font-bold" data-id="element-938">Commercial Applications</h2>
              <span data-id="element-939">{activeSection === 'safety' ? '−' : '+'}</span>
            </button>
            {activeSection === 'safety' && <div className="p-4" data-id="element-940">
                <p className="text-gray-600 mb-8" data-id="element-941">
                  Spora's standardized production system creates scalable
                  opportunities for distribution partners.
                </p>
                <div className="space-y-6" data-id="element-942">
                  <div data-id="element-943">
                    <h3 className="text-lg font-bold mb-3" data-id="element-944">
                      Standardized Dosing
                    </h3>
                    <p className="text-gray-600 text-sm" data-id="element-945">
                      Precisely calibrated doses ensure consistent effects for
                      consumers, enabling clear marketing claims.
                    </p>
                  </div>
                  <div data-id="element-946">
                    <h3 className="text-lg font-bold mb-3" data-id="element-947">
                      Regulatory Compliance
                    </h3>
                    <p className="text-gray-600 text-sm" data-id="element-948">
                      Less than 0.5% ABV, meeting FDA standards for
                      non-alcoholic beverages with unrestricted distribution.
                    </p>
                  </div>
                  <div data-id="element-949">
                    <h3 className="text-lg font-bold mb-3" data-id="element-950">
                      Intellectual Property
                    </h3>
                    <p className="text-gray-600 text-sm" data-id="element-951">
                      Protected by pending patents and trade secrets, creating a
                      defensible market position.
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* CTA Section */}
        <div className="mt-16 text-center" data-id="element-952">
          <h2 className="text-2xl font-bold mb-4" data-id="element-953">
            Become a Distribution Partner
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-954">
            Join us in bringing The Third Liquid Culture™ to market. Spora
            offers a unique opportunity to introduce a category-defining
            functional beverage with proprietary technology and defensible IP.
          </p>
          <Link to="/faq" className="bg-black text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors inline-block" data-id="element-955">
            Partner With Us
          </Link>
        </div>
      </div>
    </div>;
};
export default HowItWorks;